import { MouseEvent, ReactNode, useEffect, useId, useState } from 'react';

import { InfiniteData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, darken, keyframes, lighten, Theme } from '@mui/material/styles';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';

import {
  ApiClient,
  fetchInboxCounts,
  fetchInboxPage,
  fetchMyNotificationPreferences,
  InboxCounts,
  InboxItem,
  InboxPage,
  isInboxMutableCategory,
  LinkIntent,
  markAllInboxRead,
  markInboxRead,
  markInboxUnread,
  NotificationPreference,
  parseLinkIntent,
  setInboxActionDone,
  setMyNotificationPreference
} from '@zercode/api-client';

// ==============================|| NOTIFICATION BELL + DRAWER (NOTIF-DRAWER) ||============================== //
//
// The header bell all three persona SPAs mount: a count tile that grows out of the bell when something is
// unread, and a full-height drawer from the right with All / Unread / Action needed, per-row Open, mark read /
// unread, mark done / reopen, and a per-category in-app mute.
//
// ⚠ COPY-FREE, like PhoneField and MyOrderDetail: every string arrives through `labels`, so each app keeps
// its own catalogue keys. Navigation arrives as callbacks (`openLink`, `onOpenInbox`, `onOpenPreferences`) so
// this package needs no router — and an app WITHOUT an inbox page (teacher) simply omits it; the drawer pages
// through the whole inbox itself ("Load more"), so nothing is unreachable.
//
// ⚠ LIVE UPDATES ARE THE HOST'S JOB. `useInboxStream` lives in @zercode/auth, which this package does not
// depend on; the host subscribes and invalidates `NOTIFICATION_BELL_QUERY_KEY`.
//
// Phase 2 (ZerEduService mig 590): each row may name the RESOURCE it is about (share-link kind + id) and may
// be a TO-DO. A linked row's click opens it; an unlinked row's click expands its text. "Action needed" lists
// open to-dos, read or not — reading a refund request does not settle it, so done is its own flag.

/** Prefix of every query this component owns — invalidate it from the host's SSE handler. */
export const NOTIFICATION_BELL_QUERY_KEY = ['notif-bell'] as const;
const COUNTS_KEY = [...NOTIFICATION_BELL_QUERY_KEY, 'counts'] as const;
const LIST_KEY = [...NOTIFICATION_BELL_QUERY_KEY, 'inbox'] as const;
/** Same key the preferences pages use, so a mute here shows up there without a reload. */
const PREFERENCES_KEY = ['notif-preferences'] as const;

const PAGE_SIZE = 20;
/** Punctuation between two already-translated parts, not copy. */
const SEP = ' · ';
const IN_APP = 'inapp';

export interface NotificationBellLabels {
  /** The bell's accessible name, including the count ("Notifications, 4 unread"). */
  bellAria: (unread: number) => string;
  title: string;
  close: string;
  settings: string;
  tabAll: string;
  tabUnread: string;
  /** The to-do tab: "Action needed". */
  tabActions: string;
  groupToday: string;
  groupYesterday: string;
  groupEarlier: string;
  moreActions: string;
  /** Open the resource the message is about. */
  open: string;
  markRead: string;
  markUnread: string;
  markAllRead: string;
  /** Close a to-do. */
  markDone: string;
  /** Reopen a closed to-do. */
  reopen: string;
  /** Meta label on an open to-do. */
  actionNeeded: string;
  /** Meta label on a closed to-do. */
  actionDone: string;
  /** "Turn off {category} notifications" */
  muteCategory: (category: string) => string;
  /** "Turn on {category} notifications" */
  unmuteCategory: (category: string) => string;
  /** "You won't get new {category} notifications here." */
  mutedNotice: (category: string) => string;
  /** "{category} notifications are on again." */
  unmutedNotice: (category: string) => string;
  undo: string;
  emptyAllTitle: string;
  emptyAllBody: string;
  emptyUnreadTitle: string;
  emptyUnreadBody: string;
  emptyActionsTitle: string;
  emptyActionsBody: string;
  loadMore: string;
  loadError: string;
  retry: string;
  fullInbox: string;
  /** Display name of a wire category (`exam` → "Exams"). Unknown categories should get a sensible fallback. */
  category: (category: string) => string;
}

export interface NotificationBellProps {
  client: ApiClient;
  labels: NotificationBellLabels;
  /** `intl.locale` — relative times and dates follow the UI language, never the tr-TR default. */
  locale: string;
  /**
   * Open the resource a message is about. The host decides HOW: inside its own router when the kind has a page
   * in this app (`linkPathIn`), otherwise through the share link on the login origin. Omit it and no row opens.
   */
  openLink?: (intent: LinkIntent) => void;
  /** Omit when the app has no inbox page; the footer link is then not rendered. */
  onOpenInbox?: () => void;
  /** Omit when the app has no preferences page. */
  onOpenPreferences?: () => void;
  /** Called after every write settles, so the host can refresh its own inbox page's queries. */
  onInboxChanged?: () => void;
}

type Tab = 'all' | 'unread' | 'actions';
type Role = 'primary' | 'secondary' | 'success' | 'error' | 'grey';

const CATEGORY_LOOK: Record<string, { icon: typeof InfoOutlinedIcon; role: Role }> = {
  security: { icon: ShieldOutlinedIcon, role: 'error' },
  tenant_lifecycle: { icon: ApartmentOutlinedIcon, role: 'secondary' },
  billing: { icon: ReceiptLongOutlinedIcon, role: 'success' },
  system: { icon: InfoOutlinedIcon, role: 'grey' },
  event_reminder: { icon: EventOutlinedIcon, role: 'primary' },
  appointment: { icon: EventAvailableOutlinedIcon, role: 'primary' },
  event_invitation: { icon: MailOutlineIcon, role: 'secondary' },
  offer_decision: { icon: LocalOfferOutlinedIcon, role: 'success' },
  role_request: { icon: BadgeOutlinedIcon, role: 'secondary' },
  payment_decision: { icon: PaymentsOutlinedIcon, role: 'success' },
  event_registration: { icon: HowToRegOutlinedIcon, role: 'primary' },
  exam: { icon: AssignmentOutlinedIcon, role: 'secondary' },
  live_session: { icon: VideocamOutlinedIcon, role: 'primary' }
};
const FALLBACK_LOOK = { icon: NotificationsNoneOutlinedIcon, role: 'grey' as Role };

// ------------------------------------------------------------------ pure helpers (exported for tests)

/**
 * "now", "5 minutes ago", "yesterday", "3 days ago", then a short date. The language is `locale`'s —
 * ⚠ never the argument-less form, which prints the BROWSER's language (`pnpm i18n:locale-args`).
 */
export function formatNotificationTime(iso: string, nowMs: number, locale: string): string {
  const at = new Date(iso);
  const seconds = Math.round((at.getTime() - nowMs) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'long' });
  const abs = Math.abs(seconds);
  if (abs < 45) return rtf.format(0, 'second');
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute');
  if (abs < 86_400) return rtf.format(Math.round(seconds / 3600), 'hour');
  if (abs < 7 * 86_400) return rtf.format(Math.round(seconds / 86_400), 'day');
  const sameYear = at.getFullYear() === new Date(nowMs).getFullYear();
  return at.toLocaleDateString(locale, { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
}

export type NotificationGroup = 'today' | 'yesterday' | 'earlier';

/** Calendar-day bucket in the reader's own time zone — "yesterday" means before midnight, not 24h ago. */
export function notificationGroup(iso: string, nowMs: number): NotificationGroup {
  const startOfToday = new Date(nowMs);
  startOfToday.setHours(0, 0, 0, 0);
  const at = new Date(iso).getTime();
  if (at >= startOfToday.getTime()) return 'today';
  if (at >= startOfToday.getTime() - 86_400_000) return 'yesterday';
  return 'earlier';
}

/**
 * The rows a FILTERED tab (Unread, Action needed) shows. ⚠ A row that left the filter DURING this visit —
 * read in Unread, marked done in Action needed — stays on screen (now styled as such) until the tab or the
 * drawer changes: otherwise the refetch that follows the click removes the very row the reader just acted on,
 * and the Undo-shaped "Reopen" in its menu with it.
 */
export function keptTabRows(serverRows: InboxItem[], kept: ReadonlyMap<string, InboxItem>): InboxItem[] {
  const byId = new Map(serverRows.map((row) => [row.id, row]));
  kept.forEach((row, id) => byId.set(id, row));
  return [...byId.values()].sort((a, b) => b.createdAtUtc.localeCompare(a.createdAtUtc));
}

/** The resource a row is about, when this build knows its kind and the id is a GUID; otherwise null. */
export function rowLink(row: Pick<InboxItem, 'linkKind' | 'linkId'>): LinkIntent | null {
  return parseLinkIntent(row.linkKind ?? undefined, row.linkId ?? undefined);
}

/** An open to-do: requires action and nobody has marked it done. */
export function isOpenAction(row: Pick<InboxItem, 'requiresAction' | 'actionDoneAtUtc'>): boolean {
  return row.requiresAction && !row.actionDoneAtUtc;
}

function patchPages(data: InfiniteData<InboxPage> | undefined, patch: (row: InboxItem) => InboxItem) {
  if (!data) return data;
  return { ...data, pages: data.pages.map((page) => ({ ...page, items: page.items.map(patch) })) };
}

function isMuted(prefs: NotificationPreference[] | undefined, category: string): boolean {
  return !!prefs?.some((p) => p.channel === IN_APP && p.category === category && p.optedOut);
}

// ------------------------------------------------------------------ styles

const ring = keyframes`
  0%, 100% { transform: rotate(0); }
  15% { transform: rotate(16deg); }
  30% { transform: rotate(-14deg); }
  45% { transform: rotate(10deg); }
  60% { transform: rotate(-6deg); }
  75% { transform: rotate(3deg); }
`;

function avatarSx(role: Role) {
  return (theme: Theme) => {
    const p = theme.palette;
    if (role === 'grey') {
      return {
        bgcolor: p.grey[100],
        color: p.grey[700],
        ...theme.applyStyles('dark', { bgcolor: alpha(p.grey[500], 0.16), color: p.grey[300] })
      };
    }
    return {
      bgcolor: alpha(p[role].main, 0.12),
      color: p[role].dark,
      ...theme.applyStyles('dark', { bgcolor: alpha(p[role].main, 0.22), color: p[role].light })
    };
  };
}

/**
 * "Action needed" ink. ⚠ NEVER `warning.main`/`warning.dark` AS-IS on white — Berry's warning palette is
 * unreadable as text there (~1.3–1.9:1, zercode-spa-layout-and-i18n rule 8). Computed from the palette so it
 * survives a tenant theme:
 *   light — warning.dark darkened by half (Berry: ≈ #806004, ~5.8:1 on white). Alert's own formula on
 *     warning.light was tried first and came out a neutral grey (#66635a): readable, but no longer amber, and
 *     amber is the whole signal here.
 *   dark  — warning.main, which is a light yellow and reads well on the dark paper; lightening warning.light
 *     (the Alert formula's dark branch) came out near-white, indistinguishable from the caption beside it.
 */
const actionInk = (theme: Theme) => ({
  color: darken(theme.palette.warning.dark, 0.5),
  ...theme.applyStyles('dark', { color: lighten(theme.palette.warning.main, 0.1) })
});

// ------------------------------------------------------------------ component

export function NotificationBell({
  client,
  labels,
  locale,
  openLink,
  onOpenInbox,
  onOpenPreferences,
  onInboxChanged
}: NotificationBellProps) {
  const queryClient = useQueryClient();
  const titleId = useId();

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('all');
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [kept, setKept] = useState<ReadonlyMap<string, InboxItem>>(new Map());
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [menu, setMenu] = useState<{ anchor: HTMLElement; item: InboxItem } | null>(null);
  const [notice, setNotice] = useState<{ category: string; optedOut: boolean } | null>(null);

  // badge + Action needed count — polled as a safety net; the host's SSE subscription is the live path
  const countsQuery = useQuery({
    queryKey: COUNTS_KEY,
    queryFn: () => fetchInboxCounts(client),
    refetchInterval: 120_000
  });
  const unread = countsQuery.data?.unread ?? 0;
  const openActions = countsQuery.data?.openActions ?? 0;

  // ring once when the count GOES UP — adjust-during-render, not an effect. `null` until the first answer,
  // so the initial load (0 → 4) does not ring.
  const [seenCount, setSeenCount] = useState<number | null>(null);
  const [ringKey, setRingKey] = useState(0);
  if (countsQuery.data !== undefined && countsQuery.data.unread !== seenCount) {
    if (seenCount !== null && countsQuery.data.unread > seenCount) setRingKey((k) => k + 1);
    setSeenCount(countsQuery.data.unread);
  }

  const listQuery = useInfiniteQuery({
    queryKey: [...LIST_KEY, tab],
    enabled: open,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      fetchInboxPage(client, {
        page: pageParam,
        pageSize: PAGE_SIZE,
        onlyUnread: tab === 'unread',
        onlyOpenActions: tab === 'actions'
      }),
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, page) => n + page.items.length, 0);
      return last.items.length > 0 && loaded < last.totalItems ? all.length + 1 : undefined;
    }
  });

  const prefsQuery = useQuery({
    queryKey: PREFERENCES_KEY,
    enabled: open,
    queryFn: () => fetchMyNotificationPreferences(client)
  });

  // keep relative times honest while the drawer stays open
  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, [open]);

  const settle = () => {
    void queryClient.invalidateQueries({ queryKey: NOTIFICATION_BELL_QUERY_KEY });
    onInboxChanged?.();
  };

  /** Optimistic: patch every cached list, the counts, and keep the row on a filtered tab it just left. */
  const patchRow = (item: InboxItem, patched: InboxItem) => {
    queryClient.setQueriesData<InfiniteData<InboxPage>>({ queryKey: LIST_KEY }, (d) =>
      patchPages(d, (row) => (row.id === item.id ? patched : row))
    );
    queryClient.setQueryData<InboxCounts>(COUNTS_KEY, (c) =>
      c
        ? {
            unread: Math.max(0, c.unread + (item.isRead === patched.isRead ? 0 : patched.isRead ? -1 : 1)),
            openActions: Math.max(0, c.openActions + (isOpenAction(item) === isOpenAction(patched) ? 0 : isOpenAction(patched) ? 1 : -1))
          }
        : c
    );
    const leftFilter = (tab === 'unread' && patched.isRead) || (tab === 'actions' && !isOpenAction(patched));
    if (tab !== 'all') {
      setKept((prev) => {
        const next = new Map(prev);
        if (leftFilter) next.set(item.id, patched);
        else next.delete(item.id);
        return next;
      });
    }
  };

  const stamp = () => new Date().toISOString();

  const readMutation = useMutation({
    mutationFn: (item: InboxItem) => markInboxRead(client, item.id),
    onMutate: (item) => patchRow(item, { ...item, isRead: true, readAtUtc: stamp() }),
    onSettled: settle
  });
  const unreadMutation = useMutation({
    mutationFn: (item: InboxItem) => markInboxUnread(client, item.id),
    onMutate: (item) => patchRow(item, { ...item, isRead: false, readAtUtc: null }),
    onSettled: settle
  });
  const doneMutation = useMutation({
    mutationFn: ({ item, done }: { item: InboxItem; done: boolean }) => setInboxActionDone(client, item.id, done),
    // done implies read; reopening does not imply unread (the server does the same)
    onMutate: ({ item, done }) =>
      patchRow(
        item,
        done
          ? { ...item, actionDoneAtUtc: stamp(), isRead: true, readAtUtc: item.readAtUtc ?? stamp() }
          : { ...item, actionDoneAtUtc: null }
      ),
    onSettled: settle
  });
  const readAllMutation = useMutation({
    mutationFn: () => markAllInboxRead(client),
    onMutate: () => {
      const at = stamp();
      if (tab === 'unread') {
        setKept((prev) => {
          const next = new Map(prev);
          rows.forEach((row) => next.set(row.id, { ...row, isRead: true, readAtUtc: row.readAtUtc ?? at }));
          return next;
        });
      }
      queryClient.setQueriesData<InfiniteData<InboxPage>>({ queryKey: LIST_KEY }, (d) =>
        patchPages(d, (row) => (row.isRead ? row : { ...row, isRead: true, readAtUtc: at }))
      );
      queryClient.setQueryData<InboxCounts>(COUNTS_KEY, (c) => (c ? { ...c, unread: 0 } : c));
    },
    onSettled: settle
  });
  const muteMutation = useMutation({
    mutationFn: (pref: { category: string; optedOut: boolean }) =>
      setMyNotificationPreference(client, { category: pref.category, channel: IN_APP, optedOut: pref.optedOut }),
    onSuccess: (_saved, pref) => setNotice(pref),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: PREFERENCES_KEY })
  });

  const serverRows = listQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const rows = tab === 'all' ? serverRows : keptTabRows(serverRows, kept);

  const openDrawer = () => {
    setNowMs(Date.now());
    setOpen(true);
  };
  const closeDrawer = () => {
    setOpen(false);
    setMenu(null);
    setNotice(null);
    setKept(new Map());
    setExpanded(new Set());
  };
  const changeTab = (next: Tab) => {
    setTab(next);
    setKept(new Map());
    setExpanded(new Set());
  };

  const openResource = (item: InboxItem, intent: LinkIntent) => {
    if (!item.isRead) readMutation.mutate(item);
    closeDrawer();
    openLink?.(intent);
  };

  const onRowClick = (item: InboxItem) => {
    const intent = openLink ? rowLink(item) : null;
    if (intent) {
      openResource(item, intent);
      return;
    }
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
    if (!item.isRead) readMutation.mutate(item);
  };

  const openMenu = (event: MouseEvent<HTMLElement>, item: InboxItem) => {
    event.stopPropagation();
    setMenu({ anchor: event.currentTarget, item });
  };

  // ---------------------------------------------------------------- render pieces

  const groupLabel: Record<NotificationGroup, string> = {
    today: labels.groupToday,
    yesterday: labels.groupYesterday,
    earlier: labels.groupEarlier
  };
  const grouped: { group: NotificationGroup; items: InboxItem[] }[] = [];
  rows.forEach((row) => {
    const group = notificationGroup(row.createdAtUtc, nowMs);
    const last = grouped[grouped.length - 1];
    if (last?.group === group) last.items.push(row);
    else grouped.push({ group, items: [row] });
  });

  const empty = {
    all: { title: labels.emptyAllTitle, body: labels.emptyAllBody, icon: <NotificationsOffOutlinedIcon fontSize="large" /> },
    unread: { title: labels.emptyUnreadTitle, body: labels.emptyUnreadBody, icon: <DoneAllIcon fontSize="large" /> },
    actions: { title: labels.emptyActionsTitle, body: labels.emptyActionsBody, icon: <TaskAltIcon fontSize="large" /> }
  }[tab];

  let body: ReactNode;
  if (listQuery.isPending) {
    body = (
      <Stack sx={{ gap: 2, p: 2 }} aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <Stack key={i} direction="row" sx={{ gap: 1.5 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="70%" />
              <Skeleton width="95%" />
              <Skeleton width="35%" />
            </Box>
          </Stack>
        ))}
      </Stack>
    );
  } else if (listQuery.isError) {
    body = (
      <Alert
        severity="error"
        sx={{ m: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => void listQuery.refetch()}>
            {labels.retry}
          </Button>
        }
      >
        {labels.loadError}
      </Alert>
    );
  } else if (rows.length === 0) {
    body = (
      <Stack sx={{ alignItems: 'center', textAlign: 'center', px: 3, py: 6, gap: 1 }}>
        <Avatar sx={(theme) => ({ width: 64, height: 64, mb: 1, ...avatarSx('primary')(theme) })}>{empty.icon}</Avatar>
        <Typography variant="subtitle1">{empty.title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {empty.body}
        </Typography>
      </Stack>
    );
  } else {
    body = (
      <>
        {grouped.map(({ group, items }) => (
          <List
            key={group}
            disablePadding
            subheader={
              <ListSubheader
                sx={{ lineHeight: '36px', fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.disabled' }}
              >
                {groupLabel[group]}
              </ListSubheader>
            }
          >
            {items.map((item) => {
              const look = CATEGORY_LOOK[item.category] ?? FALLBACK_LOOK;
              const Icon = look.icon;
              const isOpen = expanded.has(item.id);
              const intent = openLink ? rowLink(item) : null;
              const openTodo = isOpenAction(item);
              return (
                <ListItem
                  key={item.id}
                  disablePadding
                  sx={(theme) => ({
                    px: 1,
                    display: 'block',
                    position: 'relative',
                    '& .row-more': { opacity: { md: 0 } },
                    '&:hover .row-more, & .row-more:focus-visible, & .row-more[aria-expanded="true"]': { opacity: 1 },
                    '& .row-surface': {
                      borderRadius: 2,
                      ...(!item.isRead && {
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                        ...theme.applyStyles('dark', { bgcolor: alpha(theme.palette.primary.main, 0.12) })
                      })
                    }
                  })}
                >
                  <Box className="row-surface">
                    <ListItemButton
                      onClick={() => onRowClick(item)}
                      aria-expanded={intent ? undefined : isOpen}
                      sx={{ alignItems: 'flex-start', borderRadius: 2, gap: 1.5, py: 1.25, pr: '52px' }}
                    >
                      <Avatar sx={(theme) => ({ width: 40, height: 40, mt: 0.25, ...avatarSx(look.role)(theme) })}>
                        <Icon fontSize="small" />
                      </Avatar>
                      <ListItemText
                        sx={{ my: 0, minWidth: 0 }}
                        primary={item.title}
                        slotProps={{
                          primary: { variant: 'body1', sx: { fontWeight: item.isRead ? 400 : 600, color: 'text.primary', lineHeight: 1.35 } },
                          secondary: { component: 'div' }
                        }}
                        secondary={
                          <>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mt: 0.25,
                                whiteSpace: 'pre-wrap',
                                ...(!isOpen && { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' })
                              }}
                            >
                              {item.body}
                            </Typography>
                            <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, mt: 0.5, color: 'text.disabled', flexWrap: 'wrap' }}>
                              <AccessTimeIcon sx={{ fontSize: 14 }} />
                              <Typography variant="caption" color="inherit">
                                {[formatNotificationTime(item.createdAtUtc, nowMs, locale), labels.category(item.category)].join(SEP)}
                              </Typography>
                              {item.requiresAction && (
                                <Typography
                                  variant="caption"
                                  sx={(theme) => ({
                                    fontWeight: 600,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.25,
                                    ...(openTodo ? actionInk(theme) : { color: theme.palette.success.dark })
                                  })}
                                >
                                  {SEP}
                                  {!openTodo && <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                                  {openTodo ? labels.actionNeeded : labels.actionDone}
                                </Typography>
                              )}
                            </Stack>
                          </>
                        }
                      />
                      {!item.isRead && (
                        <Box
                          component="span"
                          aria-hidden
                          sx={{ position: 'absolute', right: 44, top: 18, width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }}
                        />
                      )}
                    </ListItemButton>
                    {/* ⚠ OUTSIDE the ListItemButton: a button inside a button is an a11y violation (nested-interactive). */}
                    {openTodo && (
                      <Stack direction="row" sx={{ gap: 1, pl: '68px', pb: 1.25, pr: 2 }}>
                        {intent && (
                          <Button size="small" variant="contained" disableElevation onClick={() => openResource(item, intent)}>
                            {labels.open}
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DoneIcon />}
                          onClick={() => doneMutation.mutate({ item, done: true })}
                        >
                          {labels.markDone}
                        </Button>
                      </Stack>
                    )}
                  </Box>
                  <IconButton
                    className="row-more"
                    size="small"
                    aria-label={labels.moreActions}
                    aria-haspopup="menu"
                    aria-expanded={menu?.item.id === item.id}
                    onClick={(e) => openMenu(e, item)}
                    sx={{ position: 'absolute', right: 14, top: 8 }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </ListItem>
              );
            })}
          </List>
        ))}
        {listQuery.hasNextPage && (
          <Box sx={{ p: 1.5, textAlign: 'center' }}>
            <Button size="small" onClick={() => void listQuery.fetchNextPage()} disabled={listQuery.isFetchingNextPage}>
              {labels.loadMore}
            </Button>
          </Box>
        )}
      </>
    );
  }

  const menuItem = menu?.item;
  const menuIntent = menuItem && openLink ? rowLink(menuItem) : null;
  const menuCategory = menuItem && isInboxMutableCategory(menuItem.category) ? menuItem.category : null;
  const menuCategoryMuted = menuCategory ? isMuted(prefsQuery.data, menuCategory) : false;
  const unreadText = unread > 99 ? '99+' : String(unread);
  const actionsText = openActions > 99 ? '99+' : String(openActions);

  return (
    <>
      <Box sx={{ ml: 2 }}>
        <ButtonBase
          onClick={() => (open ? closeDrawer() : openDrawer())}
          aria-label={labels.bellAria(unread)}
          aria-haspopup="dialog"
          aria-expanded={open}
          // ⚠ SAME TILE AS ITS NEIGHBOURS: the Berry header buttons (fullscreen, language, …) are 34px
          // squares with an 8px radius on primary.light — typography.commonAvatar/mediumAvatar. A round pill
          // and a bare icon at zero read as a foreign control in that row, so only the WIDTH (the count)
          // and the TINT (red while something is unread) differ.
          sx={(theme) => ({
            height: 34,
            minWidth: 34,
            px: unread > 0 ? 1 : 0,
            gap: 0.5,
            borderRadius: '8px',
            typography: 'subtitle2',
            fontWeight: 600,
            transition: 'background-color .2s, color .2s',
            ...(unread > 0
              ? {
                  bgcolor: alpha(theme.palette.error.main, 0.12),
                  color: theme.palette.error.dark,
                  '&:hover, &[aria-expanded="true"]': { bgcolor: theme.palette.error.main, color: theme.palette.error.contrastText },
                  ...theme.applyStyles('dark', {
                    bgcolor: alpha(theme.palette.error.main, 0.2),
                    color: theme.palette.error.light
                  })
                }
              : {
                  bgcolor: theme.palette.primary.light,
                  color: theme.palette.primary.dark,
                  '&:hover, &[aria-expanded="true"]': { bgcolor: theme.palette.primary.main, color: theme.palette.primary.light },
                  // Berry's own `dark` palette entry, as the neighbouring tiles use; MUI's default theme
                  // (the a11y test kits) has none, hence the fallback.
                  ...theme.applyStyles('dark', {
                    bgcolor: (theme.palette as { dark?: { main: string } }).dark?.main ?? alpha(theme.palette.primary.main, 0.16),
                    color: theme.palette.primary.dark
                  })
                }),
            '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 }
          })}
        >
          <Box
            key={ringKey}
            component="span"
            sx={{
              display: 'inline-flex',
              transformOrigin: '50% 10%',
              ...(ringKey > 0 && { animation: `${ring} .9s ease`, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } })
            }}
          >
            <NotificationsNoneOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          {unread > 0 && (
            <Box component="span" aria-hidden>
              {unreadText}
            </Box>
          )}
        </ButtonBase>
      </Box>

      <Drawer
        anchor="right"
        open={open}
        onClose={closeDrawer}
        slotProps={{ paper: { sx: { width: { xs: '100%', sm: 440 }, display: 'flex', flexDirection: 'column' }, role: 'dialog', 'aria-labelledby': titleId } }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography id={titleId} variant="h3" sx={{ flex: 1 }}>
            {labels.title}
          </Typography>
          {onOpenPreferences && (
            <Tooltip title={labels.settings}>
              <IconButton
                aria-label={labels.settings}
                onClick={() => {
                  closeDrawer();
                  onOpenPreferences();
                }}
              >
                <SettingsOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton aria-label={labels.close} onClick={closeDrawer}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <ToggleButtonGroup exclusive fullWidth size="small" value={tab} onChange={(_e, next: Tab | null) => next && changeTab(next)}>
            <ToggleButton value="all">{labels.tabAll}</ToggleButton>
            <ToggleButton value="unread">{unread > 0 ? [labels.tabUnread, unreadText].join(SEP) : labels.tabUnread}</ToggleButton>
            <ToggleButton value="actions">{openActions > 0 ? [labels.tabActions, actionsText].join(SEP) : labels.tabActions}</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {notice && (
          <Alert
            severity={notice.optedOut ? 'info' : 'success'}
            onClose={() => setNotice(null)}
            sx={{ mx: 2, mb: 1 }}
            action={
              notice.optedOut ? (
                <Button
                  color="inherit"
                  size="small"
                  disabled={muteMutation.isPending}
                  onClick={() => muteMutation.mutate({ category: notice.category, optedOut: false })}
                >
                  {labels.undo}
                </Button>
              ) : undefined
            }
          >
            {notice.optedOut ? labels.mutedNotice(labels.category(notice.category)) : labels.unmutedNotice(labels.category(notice.category))}
          </Alert>
        )}

        <Box sx={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain', pb: 1 }}>{body}</Box>

        <Divider />
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1 }}>
          <Button size="small" startIcon={<DoneAllIcon />} disabled={unread === 0 || readAllMutation.isPending} onClick={() => readAllMutation.mutate()}>
            {labels.markAllRead}
          </Button>
          {onOpenInbox && (
            <Button
              size="small"
              endIcon={<ArrowForwardIcon />}
              onClick={() => {
                closeDrawer();
                onOpenInbox();
              }}
            >
              {labels.fullInbox}
            </Button>
          )}
        </Stack>

        <Menu
          anchorEl={menu?.anchor}
          open={!!menu}
          onClose={() => setMenu(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {menuItem && menuIntent && (
            <MenuItem
              onClick={() => {
                setMenu(null);
                openResource(menuItem, menuIntent);
              }}
            >
              <ListItemIcon>
                <OpenInNewIcon fontSize="small" />
              </ListItemIcon>
              {labels.open}
            </MenuItem>
          )}
          {menuItem && (
            <MenuItem
              onClick={() => {
                setMenu(null);
                if (menuItem.isRead) unreadMutation.mutate(menuItem);
                else readMutation.mutate(menuItem);
              }}
            >
              <ListItemIcon>{menuItem.isRead ? <MarkEmailUnreadOutlinedIcon fontSize="small" /> : <DoneIcon fontSize="small" />}</ListItemIcon>
              {menuItem.isRead ? labels.markUnread : labels.markRead}
            </MenuItem>
          )}
          {menuItem?.requiresAction && (
            <MenuItem
              onClick={() => {
                setMenu(null);
                doneMutation.mutate({ item: menuItem, done: isOpenAction(menuItem) });
              }}
            >
              <ListItemIcon>{isOpenAction(menuItem) ? <TaskAltIcon fontSize="small" /> : <ReplayIcon fontSize="small" />}</ListItemIcon>
              {isOpenAction(menuItem) ? labels.markDone : labels.reopen}
            </MenuItem>
          )}
          {menuCategory && <Divider />}
          {menuCategory && (
            <MenuItem
              disabled={prefsQuery.isPending || muteMutation.isPending}
              onClick={() => {
                setMenu(null);
                muteMutation.mutate({ category: menuCategory, optedOut: !menuCategoryMuted });
              }}
            >
              <ListItemIcon>
                {menuCategoryMuted ? <NotificationsActiveOutlinedIcon fontSize="small" /> : <NotificationsOffOutlinedIcon fontSize="small" />}
              </ListItemIcon>
              {menuCategoryMuted ? labels.unmuteCategory(labels.category(menuCategory)) : labels.muteCategory(labels.category(menuCategory))}
            </MenuItem>
          )}
        </Menu>
      </Drawer>
    </>
  );
}
